import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pick from 'lodash/pick';
import get from 'lodash/get';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { toGlobalId } from 'graphql-relay';
import PageTitle from '../../components/PageTitle';
import Toast from '../../components/Toast';
import Container from '../../components/Container';
import Header from '../../components/Header';
import ChangeProfileFormWrapper from '../../components/ChangeProfileForm';
import cognitoService from '../../cognito/cognito-service';
import { logout } from '../Authorization/actions';
import './styles.css';

const messages = defineMessages({
  changeSuccess: {
    id: 'change-profile.success',
    defaultMessage: 'You\'ve updated your account information.',
  },
  changeSuccessTitle: {
    id: 'change-profile.success-title',
    defaultMessage: 'Success!',
  },
});

class ChangeProfilePage extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object.isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        idToken: PropTypes.shape({
          email: PropTypes.string.isRequired,
        }).isRequired,
        userName: PropTypes.string.isRequired,
      }).isRequired,
    }),
    location: PropTypes.shape({
      state: PropTypes.shape({
        linkBack: PropTypes.object,
      }),
    }),
    logout: PropTypes.func.isRequired,
    node: PropTypes.object.isRequired,
    mutateHEAccount: PropTypes.func.isRequired,
  };

  state = {
    success: false,
    requesting: false,
    updateError: {},
  };

  getUser = () => {
    const { node, current } = this.props;
    const userName = get(current, 'viewer.userName');
    if (node && node.userList && userName) {
      const userId = toGlobalId('HEUser', userName);
      return node.userList.find((user) => user.id === userId || user.email === current.viewer.idToken.email);
    }
    return null;
  };

  hideSuccessMessage = () => {
    this.setState({ success: false });
  };

  _handleSave = (updatedUser) => {
    this.setState({
      requesting: true,
      updateError: {},
    });

    const requests = [];
    if (updatedUser.password) {
      requests.push(this._updatePassword(updatedUser));
    }

    const userProperties = pick(updatedUser, ['firstName', 'lastName', 'email']);
    if (Object.keys(userProperties).length > 0) {
      requests.push(this._updateAttributes(userProperties));
    }
    Promise.all(requests).then((results) => {
      const newState = {
        requesting: false,
      };
      newState.updateError = results.reduce((accumulator, result) => Object.assign(accumulator, result), {});
      if (!Object.keys(newState.updateError).length) {
        newState.success = true;
      }
      this.setState(newState);
    });
  };

  _updateAttributes(userProperties) {
    return new Promise((resolve) => {
      const input = { heAccount:
        {
          id: this.props.node.id,
          heUser: { ...userProperties, id: this.getUser().id },
        },
      };
      this.props.mutateHEAccount({ variables: { input } })
        .then(() => { this._onSuccess(resolve); })
        .catch((error) => this._onFailure(error, resolve));
    });
  }

  _onFailure = (error, resolve) => {
    if (error && error.message.includes('already exists')) {
      resolve({ userExists: true });
    } else {
      resolve({ generalError: true });
    }
  };

  _onSuccess = (resolve) => {
    resolve();
  };

  _updatePassword(updatedUser) {
    return cognitoService.getCurrentUserWithSession()
      .then((currentUser) => new Promise((resolve) => {
        currentUser.changePassword(updatedUser.password, updatedUser.newPassword, (err, result) => {
          if (err) {
            if (err.message.includes('previousPassword')) {
              resolve({ previousPasswordFailure: true });
            } else {
              resolve({ generalError: true });
            }
          } else {
            resolve();
          }
        });
      }), (err) => this.props.logout(this.props.current.viewer));
  }

  render() {
    const { updateError, requesting, success } = this.state;
    const { viewer: { enabledFeatures, uuid } } = this.props;
    const isCommunityServerlessOn = enabledFeatures?.includes('COMMUNITYSERVERLESS');

    const user = this.getUser();
    if (!user) {
      return (
        <p>
          <PageTitle title="User" category="Settings" />
          <FormattedMessage id="user.update.notFound" defaultMessage="No User Found" />
        </p>
      );
    }
    return (
      <div>
        <Container text>
          <PageTitle subTitle="Account Information" title="Settings" category="Intersect" />
          <Header as="h2" styleName="settings-header" id="right-side-heading">
            <FormattedMessage id="settings.accountinfo.header" defaultMessage="Account Information" />
          </Header>
          <Toast
            active={success}
            title={<FormattedMessage {...messages.changeSuccessTitle} />}
            description={<FormattedMessage {...messages.changeSuccess} />}
            onDeactivate={this.hideSuccessMessage}
          />
          <ChangeProfileFormWrapper
            requesting={requesting}
            user={user}
            onSave={this._handleSave}
            updateError={updateError}
            isCommunityServerlessOn={isCommunityServerlessOn}
            uuid={uuid}
            viewer={this.props?.current?.viewer}
          />
        </Container>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export const ChangeProfilePageComponent = injectIntl(ChangeProfilePage);
export default connect(mapStateToProps, { logout })(ChangeProfilePageComponent);
